import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Military-Grade Encryption',
      description: 'AES-256 encryption protects your data from any threats'
    },
    {
      icon: 'ri-lock-line',
      title: 'Zero-Log Policy',
      description: 'We never track, collect, or share your private data'
    },
    {
      icon: 'ri-speed-line',
      title: 'Lightning Fast',
      description: 'Optimized servers for maximum speed and performance'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Network',
      description: 'Access content from anywhere with our worldwide servers'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Expert support team ready to assist you anytime'
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Complete Privacy',
      description: 'Browse anonymously without leaving any digital footprint'
    }
  ];

  const faqs = [
    {
      question: 'What is a VPN and why do I need it?',
      answer: 'A VPN (Virtual Private Network) creates a secure, encrypted connection between your device and the internet. It protects your privacy, secures your data, and allows you to access content freely and safely.'
    },
    {
      question: 'How does MACHINEX VPN protect my privacy?',
      answer: 'MACHINEX VPN uses military-grade AES-256 encryption and maintains a strict zero-log policy. We never track, collect, or share your browsing data, ensuring complete anonymity online.'
    },
    {
      question: 'Will VPN slow down my internet connection?',
      answer: 'Our optimized global server network is designed for maximum speed. While encryption adds minimal overhead, most users experience no noticeable difference in their browsing speed.'
    },
    {
      question: 'Can I use MACHINEX VPN on multiple devices?',
      answer: 'Yes! MACHINEX VPN supports all major platforms including Windows, Mac, iOS, Android, and Linux. You can protect all your devices with a single subscription.'
    },
    {
      question: 'How do I get started with MACHINEX VPN?',
      answer: 'Simply download our application, create an account, and connect to any of our global servers. Our intuitive interface makes it easy to secure your connection in just one click.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-cyan-500/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/cfdc2200-42ee-4f9d-8372-ccd79e718f8b.png" alt="MACHINEX DOO" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold">MACHINEX DOO</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">VPN Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">Pricing & Contact</button>
              <button onClick={() => scrollToSection('support')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">Support</button>
            </div>
            <button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all cursor-pointer whitespace-nowrap">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-black to-slate-900">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
          }}></div>
        </div>
        
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-block mb-6 px-4 py-2 rounded-full border border-cyan-400/50 bg-cyan-400/10 backdrop-blur-sm">
            <span className="text-cyan-400 text-sm font-medium whitespace-nowrap">Enterprise-Grade VPN Solution</span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
            SECURE YOUR<br />
            <span className="bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent">DIGITAL WORLD</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto font-light">
            Military-grade encryption for absolute privacy and freedom online
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>
              Download Now
            </button>
            <button onClick={() => scrollToSection('features')} className="border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-black transition-all cursor-pointer whitespace-nowrap">
              Learn More
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-cyan-400"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-32 bg-black">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-gray-500 text-sm uppercase tracking-wider">WHO WE ARE</span>
            <h2 className="text-5xl md:text-6xl font-bold mt-4 leading-tight">
              Building the future of<br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent">private internet</span><br />
              connectivity
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div className="text-gray-300 text-lg leading-relaxed space-y-4">
              <p>
                MACHINEX DOO is a leading technology company dedicated to protecting your digital privacy and freedom. We develop cutting-edge VPN solutions that combine military-grade security with user-friendly design.
              </p>
              <p>
                Our mission is to make the internet a safer place for everyone. We believe that privacy is a fundamental right, and we're committed to providing tools that empower users to take control of their online security.
              </p>
            </div>
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-cyan-500/20 shadow-lg shadow-cyan-500/10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-mail-line text-2xl text-cyan-400"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <a href="mailto:contact@machinexdoo.com" className="text-white hover:text-cyan-400 transition-colors cursor-pointer">contact@machinexdoo.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-phone-line text-2xl text-cyan-400"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Phone</p>
                    <a href="tel:+18557746083" className="text-white hover:text-cyan-400 transition-colors cursor-pointer">+1 855 774 6083</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-cyan-400"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Location</p>
                    <p className="text-white">SLOVENSKI PUT 6A<br />Jagodina, Serbia<br />581899</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: 'ri-shield-check-line', label: 'Security' },
              { icon: 'ri-lock-line', label: 'Privacy' },
              { icon: 'ri-speed-line', label: 'Speed' },
              { icon: 'ri-global-line', label: 'Global Access' },
              { icon: 'ri-customer-service-2-line', label: '24/7 Support' }
            ].map((item, index) => (
              <div key={index} className="bg-slate-900 p-6 rounded-xl border border-slate-800 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/20 transition-all cursor-pointer group">
                <div className="w-12 h-12 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-3xl text-emerald-400 group-hover:text-cyan-400 transition-colors`}></i>
                </div>
                <p className="text-white font-semibold">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-32 bg-gradient-to-b from-black via-slate-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(6, 182, 212, 0.3) 35px, rgba(6, 182, 212, 0.3) 36px)',
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16">
            <div className="inline-block mb-4 md:mb-0 px-4 py-2 rounded-full border border-emerald-400/50 bg-emerald-400/10">
              <span className="text-emerald-400 text-sm font-medium uppercase tracking-wider whitespace-nowrap">FEATURES</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold leading-tight">
              UNCOMPROMISING<br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent">PROTECTION</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 hover:scale-105 cursor-pointer">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl">
                    <i className={`${feature.icon} text-4xl text-cyan-400`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-white">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/2d40e8544c59f4881bda977f8b2d2fc2.jpg" 
            alt="Digital Security Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold mb-6 uppercase tracking-wider">
            EXPERIENCE TRUE<br />
            <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">INTERNET FREEDOM</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 font-light">
            Join thousands of users protecting their<br />
            digital privacy with MACHINEX VPN
          </p>
          <button onClick={() => scrollToSection('contact')} className="group relative inline-flex items-center gap-4 bg-black px-8 py-5 rounded-full hover:shadow-2xl hover:shadow-emerald-500/50 transition-all cursor-pointer">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-400 rounded-full">
              <i className="ri-lock-line text-xl text-black"></i>
            </div>
            <span className="text-white font-bold text-lg uppercase whitespace-nowrap">START NOW</span>
            <i className="ri-arrow-right-up-line text-2xl text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent">GET IN TOUCH</span>
            </h2>
            <p className="text-xl text-gray-400">We're here to help with any questions</p>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-3 bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-cyan-500/20">
              <form 
                className="space-y-6" 
                data-readdy-form 
                id="contact-form"
                action="https://readdy.ai/api/form/d4o1siphk4g2rg9v0e30"
                method="POST"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  fetch('https://readdy.ai/api/form/d4o1siphk4g2rg9v0e30', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  }).then(() => {
                    alert('Message sent successfully!');
                    form.reset();
                  }).catch(() => {
                    alert('Failed to send message. Please try again.');
                  });
                }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input 
                    type="text" 
                    name="name"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors" 
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors" 
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                  <input 
                    type="text" 
                    name="subject"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors" 
                    placeholder="How can we help?"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <textarea 
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors resize-none" 
                    placeholder="Your message (max 500 characters)"
                    required
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 py-4 rounded-lg font-semibold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>

            <div className="md:col-span-2 space-y-6">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-6 text-white">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/20 rounded-lg">
                      <i className="ri-mail-line text-2xl text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Email</p>
                      <a href="mailto:contact@machinexdoo.com" className="text-white hover:text-cyan-400 transition-colors cursor-pointer">contact@machinexdoo.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/20 rounded-lg">
                      <i className="ri-phone-line text-2xl text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Phone</p>
                      <a href="tel:+18557746083" className="text-white hover:text-cyan-400 transition-colors cursor-pointer">+1 855 774 6083</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/20 rounded-lg">
                      <i className="ri-map-pin-line text-2xl text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Address</p>
                      <p className="text-white">SLOVENSKI PUT 6A<br />Jagodina, Serbia<br />581899</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-cyan-500/20">
                <h3 className="text-xl font-bold mb-4 text-white">Office Location</h3>
                <div className="w-full h-64 bg-slate-800 rounded-lg overflow-hidden">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2888.234!2d21.2567!3d43.9778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDU4JzQwLjEiTiAyMcKwMTUnMjQuMSJF!5e0!3m2!1sen!2s!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Office Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="relative py-32 bg-gradient-to-b from-black to-slate-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent">FREQUENTLY ASKED</span><br />
              QUESTIONS
            </h2>
          </div>

          <div className="space-y-4 mb-20">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-slate-700 overflow-hidden hover:border-cyan-500/50 transition-all">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                >
                  <h3 className="text-xl font-bold text-white pr-4">{faq.question}</h3>
                  <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-cyan-400 flex-shrink-0 transition-transform`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-6 pb-6 text-gray-400 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-book-open-line', title: 'Documentation', description: 'Comprehensive guides and tutorials', link: '#' },
              { icon: 'ri-video-line', title: 'Video Tutorials', description: 'Step-by-step video instructions', link: '#' },
              { icon: 'ri-team-line', title: 'Community Forum', description: 'Connect with other users', link: '#' }
            ].map((resource, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/20 transition-all hover:scale-105 cursor-pointer group">
                <div className="w-16 h-16 flex items-center justify-center bg-cyan-500/20 rounded-xl mb-4 group-hover:bg-cyan-500/30 transition-colors">
                  <i className={`${resource.icon} text-4xl text-cyan-400`}></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">{resource.title}</h3>
                <p className="text-gray-400 mb-4">{resource.description}</p>
                <a href={resource.link} className="text-cyan-400 hover:text-cyan-300 font-semibold inline-flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  Learn More <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-b from-slate-900 to-black pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/cfdc2200-42ee-4f9d-8372-ccd79e718f8b.png" alt="MACHINEX DOO" className="h-10 w-10 object-contain" />
                <span className="text-xl font-bold">MACHINEX DOO</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Enterprise VPN solutions<br />
                for modern businesses<br />
                and privacy-conscious users
              </p>
            </div>

            <div>
              <h4 className="text-gray-400 uppercase text-sm font-semibold mb-4 tracking-wider">NEWSLETTER</h4>
              <form 
                className="space-y-3" 
                data-readdy-form 
                id="newsletter-form"
                action="https://readdy.ai/api/form/d4o1siphk4g2rg9v0e3g"
                method="POST"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  fetch('https://readdy.ai/api/form/d4o1siphk4g2rg9v0e3g', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  }).then(() => {
                    alert('Successfully subscribed to newsletter!');
                    form.reset();
                  }).catch(() => {
                    alert('Subscription failed. Please try again.');
                  });
                }}
              >
                <div className="relative">
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Enter your email" 
                    className="w-full bg-transparent border-b border-gray-700 py-2 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                    required
                  />
                  <button type="submit" className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer">
                    <i className="ri-arrow-right-line text-cyan-400 hover:text-cyan-300 transition-colors"></i>
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Subscribe to our updates. <a href="#" className="text-cyan-400 hover:text-cyan-300 cursor-pointer">Privacy Policy</a>
                </p>
              </form>
            </div>

            <div>
              <h4 className="text-gray-400 uppercase text-sm font-semibold mb-4 tracking-wider">QUICK LINKS</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('features')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Features</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-gray-400 uppercase text-sm font-semibold mb-4 tracking-wider">SUPPORT</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Contact Us</button></li>
                <li><button onClick={() => scrollToSection('support')} className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Help Center</button></li>
                <li><a href="#" className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Documentation</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-12 pb-8">
            <div className="text-center mb-8">
              <h2 className="text-8xl md:text-9xl font-bold text-slate-800" style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '-0.02em' }}>
                MACHINEX
              </h2>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-500 text-sm">
              <p>&copy; 2025 MACHINEX DOO. All rights reserved.</p>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-cyan-400 transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
